import { Document, Page, Text, View, StyleSheet, PDFDownloadLink, Image } from "@react-pdf/renderer";
import { Button } from "@/components/ui/button";
import { Download, Mail } from "lucide-react";
import { useEstimates } from "@/hooks/useEstimates";
import { useEstimateLineItems } from "@/hooks/useEstimateLineItems";
import { format } from "date-fns";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface EstimatePDFProps {
  estimateId: string;
}

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 10,
    fontFamily: 'Helvetica',
  },
  header: {
    marginBottom: 30,
    borderBottom: '2 solid #2563eb',
    paddingBottom: 15,
  },
  companyName: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1e40af',
    marginBottom: 5,
  },
  companyInfo: {
    fontSize: 9,
    color: '#64748b',
    marginTop: 3,
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#1e40af',
    marginBottom: 8,
    borderBottom: '1 solid #e2e8f0',
    paddingBottom: 3,
  },
  row: {
    flexDirection: 'row',
    marginBottom: 3,
  },
  label: {
    width: '30%',
    fontWeight: 'bold',
    color: '#475569',
  },
  value: {
    width: '70%',
    color: '#1e293b',
  },
  table: {
    marginTop: 10,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f1f5f9',
    padding: 8,
    fontWeight: 'bold',
    borderBottom: '1 solid #cbd5e1',
  },
  tableRow: {
    flexDirection: 'row',
    padding: 8,
    borderBottom: '1 solid #e2e8f0',
  },
  col1: { width: '40%' },
  col2: { width: '15%', textAlign: 'right' },
  col3: { width: '15%', textAlign: 'right' },
  col4: { width: '15%', textAlign: 'right' },
  col5: { width: '15%', textAlign: 'right' },
  totalsSection: {
    marginTop: 20,
    alignItems: 'flex-end',
  },
  totalRow: {
    flexDirection: 'row',
    width: '40%',
    justifyContent: 'space-between',
    marginBottom: 5,
    paddingHorizontal: 10,
  },
  grandTotal: {
    flexDirection: 'row',
    width: '40%',
    justifyContent: 'space-between',
    marginTop: 10,
    paddingTop: 10,
    paddingHorizontal: 10,
    borderTop: '2 solid #2563eb',
    fontWeight: 'bold',
    fontSize: 12,
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 40,
    right: 40,
    fontSize: 8,
    color: '#64748b',
    borderTop: '1 solid #e2e8f0',
    paddingTop: 10,
  },
});

const EstimatePDFDocument = ({ estimate, lineItems }: any) => (
  <Document>
    <Page size="A4" style={styles.page}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.companyName}>Safeway Roofing & Chimney</Text>
        <Text style={styles.companyInfo}>Professional Roofing Services | Licensed & Insured</Text>
        <Text style={styles.companyInfo}>Phone: (555) 123-4567 | Email: info@safewayroofing.com</Text>
      </View>

      {/* Estimate Info */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Estimate Details</Text>
        <View style={styles.row}>
          <Text style={styles.label}>Estimate #:</Text>
          <Text style={styles.value}>{estimate.estimate_number}</Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.label}>Date:</Text>
          <Text style={styles.value}>{format(new Date(estimate.created_at), 'MMM dd, yyyy')}</Text>
        </View>
        {estimate.valid_until && (
          <View style={styles.row}>
            <Text style={styles.label}>Valid Until:</Text>
            <Text style={styles.value}>{format(new Date(estimate.valid_until), 'MMM dd, yyyy')}</Text>
          </View>
        )}
        <View style={styles.row}>
          <Text style={styles.label}>Status:</Text>
          <Text style={styles.value}>{estimate.status.toUpperCase()}</Text>
        </View>
      </View>

      {/* Customer Info */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Customer Information</Text>
        <View style={styles.row}>
          <Text style={styles.label}>Name:</Text>
          <Text style={styles.value}>{estimate.customer_name}</Text>
        </View>
        {estimate.customer_email && (
          <View style={styles.row}>
            <Text style={styles.label}>Email:</Text>
            <Text style={styles.value}>{estimate.customer_email}</Text>
          </View>
        )}
        <View style={styles.row}>
          <Text style={styles.label}>Phone:</Text>
          <Text style={styles.value}>{estimate.customer_phone}</Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.label}>Address:</Text>
          <Text style={styles.value}>
            {estimate.customer_address}, {estimate.city}, {estimate.county} County, {estimate.zip_code}
          </Text>
        </View>
      </View>

      {/* Line Items */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Estimate Items</Text>
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={styles.col1}>Description</Text>
            <Text style={styles.col2}>Qty</Text>
            <Text style={styles.col3}>Unit</Text>
            <Text style={styles.col4}>Unit Price</Text>
            <Text style={styles.col5}>Total</Text>
          </View>
          {lineItems.map((item: any) => (
            <View key={item.id} style={styles.tableRow}>
              <Text style={styles.col1}>{item.description}</Text>
              <Text style={styles.col2}>{item.quantity}</Text>
              <Text style={styles.col3}>{item.unit}</Text>
              <Text style={styles.col4}>${item.unit_price.toFixed(2)}</Text>
              <Text style={styles.col5}>${item.line_total.toFixed(2)}</Text>
            </View>
          ))}
        </View>
      </View>

      {/* Totals */}
      <View style={styles.totalsSection}>
        <View style={styles.totalRow}>
          <Text>Subtotal:</Text>
          <Text>${estimate.subtotal.toFixed(2)}</Text>
        </View>
        <View style={styles.totalRow}>
          <Text>Tax ({estimate.tax_rate}%):</Text>
          <Text>${estimate.tax_amount.toFixed(2)}</Text>
        </View>
        <View style={styles.grandTotal}>
          <Text>TOTAL:</Text>
          <Text>${estimate.total.toFixed(2)}</Text>
        </View>
      </View>

      {/* Notes */}
      {estimate.notes && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Additional Notes</Text>
          <Text>{estimate.notes}</Text>
        </View>
      )}

      {/* Footer */}
      <View style={styles.footer}>
        <Text>Thank you for choosing Safeway Roofing & Chimney!</Text>
        <Text>Terms: 50% deposit required to begin work. Final payment due upon completion.</Text>
        <Text>Warranty: All work includes manufacturer's warranty plus our workmanship guarantee.</Text>
      </View>
    </Page>
  </Document>
);

const EstimatePDF = ({ estimateId }: EstimatePDFProps) => {
  const [isSending, setIsSending] = useState(false);
  const { estimates } = useEstimates();
  const { lineItems } = useEstimateLineItems(estimateId);

  const estimate = estimates?.find((e: any) => e.id === estimateId);

  const handleEmailToCustomer = async () => {
    if (!estimate?.customer_email) {
      toast.error("No customer email address found");
      return;
    }

    setIsSending(true);
    try {
      const { error } = await supabase.functions.invoke('send-estimate-email', {
        body: {
          estimateId,
          customerEmail: estimate.customer_email,
        },
      });

      if (error) throw error;

      toast.success(`Estimate emailed to ${estimate.customer_email}`);
      
      // Update estimate status to sent
      await supabase
        .from('estimates')
        .update({ 
          status: 'sent',
          sent_at: new Date().toISOString(),
          email_sent_to: estimate.customer_email,
        })
        .eq('id', estimateId);

    } catch (error: any) {
      toast.error("Failed to send email: " + error.message);
    } finally {
      setIsSending(false);
    }
  };

  if (!estimate || !lineItems) {
    return <div>Loading...</div>;
  }

  return (
    <div className="flex gap-3 justify-center">
      <PDFDownloadLink
        document={<EstimatePDFDocument estimate={estimate} lineItems={lineItems} />}
        fileName={`estimate-${estimate.estimate_number}.pdf`}
      >
        {({ loading }) => (
          <Button variant="outline" disabled={loading}>
            <Download className="mr-2 h-4 w-4" />
            {loading ? 'Generating...' : 'Download PDF'}
          </Button>
        )}
      </PDFDownloadLink>
      
      <Button onClick={handleEmailToCustomer} disabled={isSending || !estimate.customer_email}>
        <Mail className="mr-2 h-4 w-4" />
        {isSending ? 'Sending...' : 'Email to Customer'}
      </Button>
    </div>
  );
};

export default EstimatePDF;
